/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComponentType } from '@angular/cdk/portal';
import { Component, effect, inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { FileDataStore } from '../../../store/file-data.store';
import { ColDef, GetRowIdFunc, GetRowIdParams, GridOptions, IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-community';
import { AgPaginatorComponent, IAgPaginator } from '../../../components/ag-grid-components/ag-paginator/ag-paginator.component';
import { CmsFileDataRequest, MorFileProcessedDataRequest } from '../../../models/mor-file-processed-data-request';
import { QueryMetadata, QuerySort } from '../../../models/query-metadata';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { AbstractControl, FormControl, FormsModule, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { GridColumnDto, GridDataType } from '../../../models/grid-data';

export class ViewProcessedRecordsDialogModel {
  readonly component: ComponentType<ViewProcessedRecordsDialogModel> = ViewProcessedRecordsDialogModel;
  panelClass?: string | string[] = ['grid-dialog-container','dialog-container'];
  autoFocus?: boolean = false;
  data: any;
  disableClose? = true;

  constructor(data: any) {
    this.data = data;
  }
}

export class ViewProcessedRecordsHeader {
  uploadDate?: string;
  payerAlias?: string;
  fileType?: string;
  uploadedBy?: string;
  id?: number;
}

export class ViewProcessedRecordsGridConfig {
  columnDefs?: ColDef[];
  rowData?: any[];
  listSize?: number;
}

@Component({
    selector: 'app-view-processed-records-dialog',
    imports: [
        MatDialogTitle,
        MatDialogContent,
        AgGridModule,
        MatIcon,
        MatInput,
        MatFormField,
        MatLabel,
        FormsModule,
        ReactiveFormsModule,
        MatButton,
        MatError
    ],
    templateUrl: './view-processed-records-dialog.component.html',
    styleUrl: './view-processed-records-dialog.component.scss'
})
export class ViewProcessedRecordsDialogComponent {
  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;
  readonly data = inject(MAT_DIALOG_DATA);
  readonly store = inject(FileDataStore);
  model: ViewProcessedRecordsHeader;
  gridOptions: GridOptions = {
    defaultColDef: {
      suppressHeaderMenuButton: true,
      suppressMovable: true
    },
    columnDefs: [],
    rowModelType: 'serverSide',
    statusBar: {
      statusPanels: [
        {
          statusPanel: AgPaginatorComponent,
          key: "ag-paginator",
        },
      ],
    },
    context: {
      componentParent: this, 
      pageSizeOptions: [1000,5000,10000,20000], 
      pageSize: 5000
    }
  }

  mbiControl = new FormControl('', [this.mbiValidator]);
  morRecordType: string = '';
  private maoColumnsInitialized = false;

  constructor(
    public dialogRef: MatDialogRef<ViewProcessedRecordsDialogComponent>
  ) {
    this.model = this.data;

    effect(() => {
      this.morRecordType = this.store.morRecordType();
    });
  }

  onGridReady(params: any) {
    this.maoColumnsInitialized = false;
    params.api?.setGridOption("serverSideDatasource", this.getServerSideDatasource());
  }

  handleClose() {
    this.dialogRef.close();
  }

  getServerSideDatasource(): IServerSideDatasource {
    return {
      getRows: (params) => {
        const pageNum = params.api?.paginationGetCurrentPage();
        const pageSize = params.api?.paginationGetPageSize();
        
        const metadata: QueryMetadata = new QueryMetadata();
        metadata.PageSize = pageSize;
        metadata.PageNumber = pageNum;

        const sortModel = params.request.sortModel;

        if(sortModel.length > 0) {
          metadata.Sort = new QuerySort();
          metadata.Sort.Sort = sortModel[0]?.sort;
          metadata.Sort.ColID = sortModel[0]?.colId;
        }

        const mbi = this.mbiControl.value ?? '';

        if (this.model.fileType?.toLowerCase() === 'mor') {
          const request = {
            MorQueueId: this.model.id,
            MBI: mbi,
            Metadata: metadata
          } as MorFileProcessedDataRequest;

          this.store.getProcessedRecordData(request)
            .then((config) => {
              if (config)
                this.setGridDataFromConfig(config, params);
            }).catch(
              (err) => {
                console.error('MOR grid request failed', err);
                if (params.api?.isDestroyed()) {
                  return;
                }
                params.api?.showNoRowsOverlay();
                params.fail();
              }
            );
        }
        if (this.model.fileType?.toLowerCase() === 'mao') {
          const request = {
            Id: this.model.id,
            MBI: mbi,
            Metadata: metadata
          } as CmsFileDataRequest;

          this.store.getCmsFileDetails(request)
            .then((response) => {
              if (response) {
                const startRow = params.request.startRow ?? (pageNum * pageSize);
                const config: ViewProcessedRecordsGridConfig = {
                  columnDefs: this.buildColumnDefsFromCmsGridResponse(response.Columns),
                  rowData: (response.Rows || []).map((row, index) => ({
                    ...row,
                    __rowId: `${startRow + index}`
                  })),
                  listSize: response.TotalRowCount
                };
                this.setGridDataFromConfig(config, params);
              }
            }).catch(
              (err) => {
                console.error('MAO grid request failed', err);
                if (params.api?.isDestroyed()) {
                  return;
                }
                params.api?.showNoRowsOverlay();
                params.fail();
              }
            );
        }
      }
    }
  }

  onClickSearchMBI(): void {
    if (this.mbiControl.valid) {
      this.agGrid?.api?.getStatusPanel<IAgPaginator>('ag-paginator')?.resetPaginator();
      this.agGrid?.api?.paginationGoToPage(0);
      this.agGrid?.api?.refreshServerSide({purge: true});
    } else {
      this.mbiControl.markAsTouched();
    }
  }

  mbiValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value as string;
    if (value.length !== 0 && value.length !== 11) {
      return { invalidLength: true };
    }
    return null;
  }

  getRowId: GetRowIdFunc = (params: GetRowIdParams) => {
    const row = params.data ?? {};

    if (row.__rowId != null) {
      return String(row.__rowId);
    }

    // MOR rows use `id`
    if (this.model.fileType?.toLowerCase() === 'mor' && row.id != null) {
      return String(row.id);
    }

    // MAO rows do not expose a single `id`; build a stable composite key.
    const compositeId = [
      row.PayerAlias,
      row.MBI ?? row.Mbi,
      row.EncounterICN,
      row.OrigEncounterICN,
      row.DOSFrom ?? row.DosFrom,
      row.DOSTo ?? row.DosTo,
      row.ICD ?? row.Icd,
      row.AddDel
    ]
      .map(value => value ?? '')
      .join('|');

    return compositeId || JSON.stringify(row);
  };

  setGridDataFromConfig(config: ViewProcessedRecordsGridConfig, params: IServerSideGetRowsParams) {
    // The response can arrive after the grid is destroyed (e.g. closing the dialog mid-request).
    if (params.api?.isDestroyed()) {
      return;
    }

    params.api?.hideOverlay();
    const isMao = this.model.fileType?.toLowerCase() === 'mao';

    if (config?.columnDefs && config.columnDefs.length > 0) {
      if (isMao) {
        if (!this.maoColumnsInitialized) {
          params.api?.setGridOption("columnDefs", config.columnDefs);
          this.maoColumnsInitialized = true;
        }
      } else {
        params.api?.setGridOption("columnDefs", config.columnDefs);
      }
    }

    try {
      params.success({
        rowData: config?.rowData ?? [],
        rowCount: config?.listSize
      });
    } catch (err) {
      console.error('Grid success callback failed', err);
      params.fail();
    }

    if (config?.listSize === 0) {
      params.api?.showNoRowsOverlay();
    }

    if (this.model.fileType?.toLowerCase() === 'mor') {
      params.api?.getStatusPanel<IAgPaginator>('ag-paginator')?.goToCurrentPage();
    }
  }

  private buildColumnDefsFromCmsGridResponse(columns: GridColumnDto[]): ColDef[] {
    if (!columns || columns.length === 0) {
      return [];
    }
    return columns
    .filter(c => !c.Hidden)
    .map(c => {
      const base: ColDef = {
        field: c.Field,
        headerName: c.Header,
        sortable: c.Sortable,
        filter: c.Filterable ? this.mapFilter(c.DataType) : false,
        resizable: true,
      };

      const typeConfig = this.mapTypeConfig(c.DataType);

      return { ...base, ...typeConfig };
    });
  }

  private mapFilter(dataType: GridDataType): ColDef['filter'] {
    switch (dataType) {
      case 'Number':
        return 'agNumberColumnFilter';
      case 'Date':
        return 'agDateColumnFilter';
      case 'Boolean':
      case 'Enum':
        return 'agSetColumnFilter';
      default:
        return 'agTextColumnFilter';
    }
  }

  private mapTypeConfig(dataType: GridDataType): Partial<ColDef> {
    switch (dataType) {
      case 'Date':
        return {
          valueFormatter: params =>
            params.value ? new Date(params.value).toLocaleDateString() : ''
        };
      default:
        return {};
    }
  }
}
